import { useQuery, useQueryClient } from "@tanstack/react-query";
import Api from "../../services/Api";
import { queryKeys } from "../query";

const HIERARCHY_URL = "/service/iqevse-csms/stations/v1/hierarchy";
const EMPTY_STATIONS = [];

/**
 * Normalise the raw hierarchy response into a consistent array of stations.
 */
function normaliseStations(raw) {
  return Array.isArray(raw) ? raw : raw?.stations || raw?.data || [];
}

/**
 * Shared React Query hook for the station hierarchy API.
 *
 * Every consumer that calls `useHierarchy()` shares the same cache entry —
 * so only ONE network request is made regardless of how many components
 * mount simultaneously (Fleet banner, Station banner, FleetMetrics table, etc.).
 *
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] — pass false to defer fetching
 * @returns {{ stations, isLoading, isError, refresh }}
 */
export function useHierarchy({ enabled = true } = {}) {
  const queryClient = useQueryClient();

  const {
    data: stations = EMPTY_STATIONS,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.charger.hierarchy(),
    queryFn: async () => {
      const raw = await Api.get(HIERARCHY_URL);
      return normaliseStations(raw);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.charger.hierarchy() });

  return { stations, isLoading, isError, refresh };
}

/**
 * Imperative getter — reads hierarchy from cache or fetches if stale.
 * Use this in non-hook contexts (e.g. async validation functions).
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @returns {Promise<Array>} stations array
 */
export async function fetchHierarchyCached(queryClient) {
  const data = await queryClient.fetchQuery({
    queryKey: queryKeys.charger.hierarchy(),
    queryFn: async () => {
      const raw = await Api.get(HIERARCHY_URL);
      return normaliseStations(raw);
    },
    staleTime: 5 * 60 * 1000,
  });
  return data;
}

export default useHierarchy;
